import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { dashboardApi, jobsApi, qk } from '@/api';

export function useJobsList(filters: jobsApi.JobsFilters = {}) {
  const key = { status: filters.status, search: filters.search };
  return useQuery({ queryKey: qk.jobs.list(key), queryFn: () => jobsApi.listJobs(filters) });
}

export function useJob(id: string) {
  return useQuery({
    queryKey: qk.jobs.detail(id),
    queryFn: () => jobsApi.getJob(id),
    enabled: !!id,
  });
}

export function useJobToDate(id: string) {
  return useQuery({
    queryKey: qk.dashboard.toDate(id),
    queryFn: () => dashboardApi.getJobToDate(id),
    enabled: !!id,
  });
}

export function useInvalidateJobs() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: qk.jobs.all() });
    void qc.invalidateQueries({ queryKey: qk.dashboard.all() });
    if (id) void qc.invalidateQueries({ queryKey: qk.jobs.detail(id) });
  };
}

export function useCreateJob() {
  const invalidate = useInvalidateJobs();
  return useMutation({ mutationFn: jobsApi.createJob, onSuccess: () => invalidate() });
}

export function usePatchJob(id: string) {
  const invalidate = useInvalidateJobs();
  return useMutation({
    mutationFn: (patch: Parameters<typeof jobsApi.patchJob>[1]) => jobsApi.patchJob(id, patch),
    onSuccess: () => invalidate(id),
  });
}

export function useSetForemen(id: string) {
  const invalidate = useInvalidateJobs();
  return useMutation({
    mutationFn: (userIds: string[]) => jobsApi.setForemen(id, userIds),
    onSuccess: () => invalidate(id),
  });
}

export function useReconcileJob() {
  const invalidate = useInvalidateJobs();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, intoJobId }: { id: string; intoJobId: string }) =>
      jobsApi.reconcileJob(id, intoJobId),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: qk.entries.all() });
      void qc.invalidateQueries({ queryKey: qk.costCodes.all() });
    },
  });
}
